/*
 * Copyright (c) 2020 Macrofocus GmbH. All Rights Reserved.
 */
package org.mkui.geom

import kotlin.math.sqrt

/**
 * This `Line2D` represents a line segment in `(x,y)`
 * coordinate space.  This class, like all of the Java 2D API, uses a
 * default coordinate system called *user space* in which the y-axis
 * values increase downward and x-axis values increase to the right.  For
 * more information on the user space coordinate system, see the
 * [
 * Coordinate Systems](http://java.sun.com/j2se/1.3/docs/guide/2d/spec/j2d-intro.fm2.html#61857) section of the Java 2D Programmer's Guide.
 *
 *
 * This class is only the abstract superclass for all objects that
 * store a 2D line segment.
 * The actual storage representation of the coordinates is left to
 * the subclass.
 *
 * @author      Jim Graham
 * @since 1.2
 */
abstract class Line2D
/**
 * This is an abstract class that cannot be instantiated directly.
 * Type-specific implementation subclasses are available for
 * instantiation and provide a number of formats for storing
 * the information necessary to satisfy the various accessory
 * methods below.
 *
 * @see java.awt.geom.Line2D.Float
 *
 * @see java.awt.geom.Line2D.Double
 *
 * @since 1.2
 */
protected constructor() : Shape {
    /**
     * A line segment specified with float coordinates.
     * @since 1.2
     */
//    class Float : Line2D {
//        /**
//         * The X coordinate of the start point of the line segment.
//         * @since 1.2
//         * @serial
//         */
//        override var x1 = 0f
//
//        /**
//         * The Y coordinate of the start point of the line segment.
//         * @since 1.2
//         * @serial
//         */
//        override var y1 = 0f
//
//        /**
//         * The X coordinate of the end point of the line segment.
//         * @since 1.2
//         * @serial
//         */
//        override var x2 = 0f
//
//        /**
//         * The Y coordinate of the end point of the line segment.
//         * @since 1.2
//         * @serial
//         */
//        override var y2 = 0f
//
//        /**
//         * Constructs and initializes a Line with coordinates (0, 0) -> (0, 0).
//         * @since 1.2
//         */
//        constructor() {}
//
//        /**
//         * Constructs and initializes a Line from the specified coordinates.
//         * @param x1 the X coordinate of the start point
//         * @param y1 the Y coordinate of the start point
//         * @param x2 the X coordinate of the end point
//         * @param y2 the Y coordinate of the end point
//         * @since 1.2
//         */
//        constructor(x1: kotlin.Float, y1: kotlin.Float, x2: kotlin.Float, y2: kotlin.Float) {
//            setLine(x1, y1, x2, y2)
//        }
//
//        /**
//         * Constructs and initializes a `Line2D` from the
//         * specified `Point2D` objects.
//         * @param p1 the start `Point2D` of this line segment
//         * @param p2 the end `Point2D` of this line segment
//         * @since 1.2
//         */
//        constructor(p1: Point2D?, p2: Point2D?) {
//            setLine(p1, p2)
//        }
//
//        /**
//         * {@inheritDoc}
//         * @since 1.2
//         */
//        override fun getX1(): kotlin.Double {
//            return x1.toDouble()
//        }
//
//        /**
//         * {@inheritDoc}
//         * @since 1.2
//         */
//        override fun getY1(): kotlin.Double {
//            return y1.toDouble()
//        }
//
//        /**
//         * {@inheritDoc}
//         * @since 1.2
//         */
//        override fun getP1(): Point2D {
//            return Float(x1, y1)
//        }
//
//        /**
//         * {@inheritDoc}
//         * @since 1.2
//         */
//        override fun getX2(): kotlin.Double {
//            return x2.toDouble()
//        }
//
//        /**
//         * {@inheritDoc}
//         * @since 1.2
//         */
//        override fun getY2(): kotlin.Double {
//            return y2.toDouble()
//        }
//
//        /**
//         * {@inheritDoc}
//         * @since 1.2
//         */
//        override fun getP2(): Point2D {
//            return Float(x2, y2)
//        }
//
//        /**
//         * {@inheritDoc}
//         * @since 1.2
//         */
//        override fun setLine(x1: kotlin.Double, y1: kotlin.Double, x2: kotlin.Double, y2: kotlin.Double) {
//            this.x1 = x1.toFloat()
//            this.y1 = y1.toFloat()
//            this.x2 = x2.toFloat()
//            this.y2 = y2.toFloat()
//        }
//
//        /**
//         * Sets the location of the end points of this `Line2D`
//         * to the specified float coordinates.
//         * @param x1 the X coordinate of the start point
//         * @param y1 the Y coordinate of the start point
//         * @param x2 the X coordinate of the end point
//         * @param y2 the Y coordinate of the end point
//         * @since 1.2
//         */
//        fun setLine(x1: kotlin.Float, y1: kotlin.Float, x2: kotlin.Float, y2: kotlin.Float) {
//            this.x1 = x1
//            this.y1 = y1
//            this.x2 = x2
//            this.y2 = y2
//        }
//
//        /**
//         * {@inheritDoc}
//         * @since 1.2
//         */
//        val bounds2D: Rectangle2D
//            get() {
//                val x: kotlin.Float
//                val y: kotlin.Float
//                val w: kotlin.Float
//                val h: kotlin.Float
//                if (x1 < x2) {
//                    x = x1
//                    w = x2 - x1
//                } else {
//                    x = x2
//                    w = x1 - x2
//                }
//                if (y1 < y2) {
//                    y = y1
//                    h = y2 - y1
//                } else {
//                    y = y2
//                    h = y1 - y2
//                }
//                return Float(x, y, w, h)
//            }
//
//        companion object {
//            /*
//         * JDK 1.6 serialVersionUID
//         */
//            private const val serialVersionUID = 6161772511649436349L
//        }
//    }

    /**
     * A line segment specified with double coordinates.
     * @since 1.2
     */
    class Double : Line2D {
        /**
         * {@inheritDoc}
         * @since 1.2
         */
        /**
         * The X coordinate of the start point of the line segment.
         * @since 1.2
         * @serial
         */
        override var x1 = 0.0
        /**
         * {@inheritDoc}
         * @since 1.2
         */
        /**
         * The Y coordinate of the start point of the line segment.
         * @since 1.2
         * @serial
         */
        override var y1 = 0.0
        /**
         * {@inheritDoc}
         * @since 1.2
         */
        /**
         * The X coordinate of the end point of the line segment.
         * @since 1.2
         * @serial
         */
        override var x2 = 0.0
        /**
         * {@inheritDoc}
         * @since 1.2
         */
        /**
         * The Y coordinate of the end point of the line segment.
         * @since 1.2
         * @serial
         */
        override var y2 = 0.0

        /**
         * Constructs and initializes a Line with coordinates (0, 0) -> (0, 0).
         * @since 1.2
         */
        constructor() {}

        /**
         * Constructs and initializes a `Line2D` from the
         * specified coordinates.
         * @param x1 the X coordinate of the start point
         * @param y1 the Y coordinate of the start point
         * @param x2 the X coordinate of the end point
         * @param y2 the Y coordinate of the end point
         * @since 1.2
         */
        constructor(x1: kotlin.Double, y1: kotlin.Double, x2: kotlin.Double, y2: kotlin.Double) {
            setLine(x1, y1, x2, y2)
        }

        /**
         * Constructs and initializes a `Line2D` from the
         * specified `Point2D` objects.
         * @param p1 the start `Point2D` of this line segment
         * @param p2 the end `Point2D` of this line segment
         * @since 1.2
         */
        constructor(p1: Point2D, p2: Point2D) {
            setLine(p1, p2)
        }

        /**
         * {@inheritDoc}
         * @since 1.2
         */
        override val p1: Point2D
            get() = Point2D.Double(x1, y1)

        /**
         * {@inheritDoc}
         * @since 1.2
         */
        override val p2: Point2D
            get() = Point2D.Double(x2, y2)

        /**
         * {@inheritDoc}
         * @since 1.2
         */
        override fun setLine(x1: kotlin.Double, y1: kotlin.Double, x2: kotlin.Double, y2: kotlin.Double) {
            this.x1 = x1
            this.y1 = y1
            this.x2 = x2
            this.y2 = y2
        }

        /**
         * {@inheritDoc}
         * @since 1.2
         */
        override val bounds2D: Rectangle2D
            get() {
                val x: kotlin.Double
                val y: kotlin.Double
                val w: kotlin.Double
                val h: kotlin.Double
                if (x1 < x2) {
                    x = x1
                    w = x2 - x1
                } else {
                    x = x2
                    w = x1 - x2
                }
                if (y1 < y2) {
                    y = y1
                    h = y2 - y1
                } else {
                    y = y2
                    h = y1 - y2
                }
                return Rectangle2D.Double(x, y, w, h)
            }

        companion object {
            /*
         * JDK 1.6 serialVersionUID
         */
            private const val serialVersionUID = 7979627399746467499L
        }
    }

    /**
     * Returns the X coordinate of the start point in double precision.
     * @return the X coordinate of the start point of this
     * `Line2D` object.
     * @since 1.2
     */
    abstract val x1: kotlin.Double

    /**
     * Returns the Y coordinate of the start point in double precision.
     * @return the Y coordinate of the start point of this
     * `Line2D` object.
     * @since 1.2
     */
    abstract val y1: kotlin.Double

    /**
     * Returns the start `Point2D` of this `Line2D`.
     * @return the start `Point2D` of this `Line2D`.
     * @since 1.2
     */
    abstract val p1: Point2D

    /**
     * Returns the X coordinate of the end point in double precision.
     * @return the X coordinate of the end point of this
     * `Line2D` object.
     * @since 1.2
     */
    abstract val x2: kotlin.Double

    /**
     * Returns the Y coordinate of the end point in double precision.
     * @return the Y coordinate of the end point of this
     * `Line2D` object.
     * @since 1.2
     */
    abstract val y2: kotlin.Double

    /**
     * Returns the end `Point2D` of this `Line2D`.
     * @return the end `Point2D` of this `Line2D`.
     * @since 1.2
     */
    abstract val p2: Point2D

    /**
     * Sets the location of the end points of this `Line2D` to
     * the specified double coordinates.
     * @param x1 the X coordinate of the start point
     * @param y1 the Y coordinate of the start point
     * @param x2 the X coordinate of the end point
     * @param y2 the Y coordinate of the end point
     * @since 1.2
     */
    abstract fun setLine(x1: kotlin.Double, y1: kotlin.Double, x2: kotlin.Double, y2: kotlin.Double)

    /**
     * Sets the location of the end points of this `Line2D` to
     * the specified `Point2D` coordinates.
     * @param p1 the start `Point2D` of the line segment
     * @param p2 the end `Point2D` of the line segment
     * @since 1.2
     */
    fun setLine(p1: Point2D, p2: Point2D) {
        setLine(p1.x, p1.y, p2.x, p2.y)
    }

    /**
     * Sets the location of the end points of this `Line2D` to
     * the same as those end points of the specified `Line2D`.
     * @param l the specified `Line2D`
     * @since 1.2
     */
    fun setLine(l: Line2D) {
        setLine(l.x1, l.y1, l.x2, l.y2)
    }

    /**
     * Returns an indicator of where the specified point
     * `(px,py)` lies with respect to this line segment.
     * See the method comments of
     * [.relativeCCW]
     * to interpret the return value.
     * @param px the X coordinate of the specified point
     * to be compared with this `Line2D`
     * @param py the Y coordinate of the specified point
     * to be compared with this `Line2D`
     * @return an integer that indicates the position of the specified
     * coordinates with respect to this `Line2D`
     * @see .relativeCCW
     * @since 1.2
     */
    fun relativeCCW(px: kotlin.Double, py: kotlin.Double): Int {
        return relativeCCW(x1, y1, x2, y2, px, py)
    }

    /**
     * Returns an indicator of where the specified `Point2D`
     * lies with respect to this line segment.
     * See the method comments of
     * [.relativeCCW]
     * to interpret the return value.
     * @param p the specified `Point2D` to be compared
     * with this `Line2D`
     * @return an integer that indicates the position of the specified
     * `Point2D` with respect to this `Line2D`
     * @see .relativeCCW
     * @since 1.2
     */
    fun relativeCCW(p: Point2D): Int {
        return relativeCCW(
            x1, y1, x2, y2,
            p.x, p.y
        )
    }

    /**
     * Tests if the line segment from `(x1,y1)` to
     * `(x2,y2)` intersects this line segment.
     *
     * @param x1 the X coordinate of the start point of the
     * specified line segment
     * @param y1 the Y coordinate of the start point of the
     * specified line segment
     * @param x2 the X coordinate of the end point of the
     * specified line segment
     * @param y2 the Y coordinate of the end point of the
     * specified line segment
     * @return <true> if this line segment and the specified line segment
     * intersect each other; `false` otherwise.
     * @since 1.2
    </true> */
    fun intersectsLine(x1: kotlin.Double, y1: kotlin.Double, x2: kotlin.Double, y2: kotlin.Double): Boolean {
        return linesIntersect(
            x1, y1, x2, y2,
            this.x1, this.y1, this.x2, this.y2
        )
    }

    /**
     * Tests if the specified line segment intersects this line segment.
     * @param l the specified `Line2D`
     * @return `true` if this line segment and the specified line
     * segment intersect each other;
     * `false` otherwise.
     * @since 1.2
     */
    fun intersectsLine(l: Line2D): Boolean {
        return linesIntersect(
            l.x1, l.y1, l.x2, l.y2,
            x1, y1, x2, y2
        )
    }

    /**
     * Returns the square of the distance from a point to this line segment.
     * The distance measured is the distance between the specified
     * point and the closest point between the current line's end points.
     * If the specified point intersects the line segment in between the
     * end points, this method returns 0.0.
     *
     * @param px the X coordinate of the specified point being
     * measured against this line segment
     * @param py the Y coordinate of the specified point being
     * measured against this line segment
     * @return a double value that is the square of the distance from the
     * specified point to the current line segment.
     * @see .ptLineDistSq
     * @since 1.2
     */
    fun ptSegDistSq(px: kotlin.Double, py: kotlin.Double): kotlin.Double {
        return ptSegDistSq(x1, y1, x2, y2, px, py)
    }

    /**
     * Returns the square of the distance from a `Point2D` to
     * this line segment.
     * The distance measured is the distance between the specified
     * point and the closest point between the current line's end points.
     * If the specified point intersects the line segment in between the
     * end points, this method returns 0.0.
     * @param pt the specified `Point2D` being measured against
     * this line segment.
     * @return a double value that is the square of the distance from the
     * specified `Point2D` to the current
     * line segment.
     * @see .ptLineDistSq
     * @since 1.2
     */
    fun ptSegDistSq(pt: Point2D): kotlin.Double {
        return ptSegDistSq(
            x1, y1, x2, y2,
            pt.x, pt.y
        )
    }

    /**
     * Returns the distance from a point to this line segment.
     * The distance measured is the distance between the specified
     * point and the closest point between the current line's end points.
     * If the specified point intersects the line segment in between the
     * end points, this method returns 0.0.
     *
     * @param px the X coordinate of the specified point being
     * measured against this line segment
     * @param py the Y coordinate of the specified point being
     * measured against this line segment
     * @return a double value that is the distance from the specified
     * point to the current line segment.
     * @see .ptLineDist
     * @since 1.2
     */
    fun ptSegDist(px: kotlin.Double, py: kotlin.Double): kotlin.Double {
        return ptSegDist(x1, y1, x2, y2, px, py)
    }

    /**
     * Returns the distance from a `Point2D` to this line
     * segment.
     * The distance measured is the distance between the specified
     * point and the closest point between the current line's end points.
     * If the specified point intersects the line segment in between the
     * end points, this method returns 0.0.
     * @param pt the specified `Point2D` being measured
     * against this line segment
     * @return a double value that is the distance from the specified
     * `Point2D` to the current line
     * segment.
     * @see .ptLineDist
     * @since 1.2
     */
    fun ptSegDist(pt: Point2D): kotlin.Double {
        return ptSegDist(
            x1, y1, x2, y2,
            pt.x, pt.y
        )
    }

    /**
     * Returns the square of the distance from a point to this line.
     * The distance measured is the distance between the specified
     * point and the closest point on the infinitely-extended line
     * defined by this `Line2D`.  If the specified point
     * intersects the line, this method returns 0.0.
     *
     * @param px the X coordinate of the specified point being
     * measured against this line
     * @param py the Y coordinate of the specified point being
     * measured against this line
     * @return a double value that is the square of the distance from a
     * specified point to the current line.
     * @see .ptSegDistSq
     * @since 1.2
     */
    fun ptLineDistSq(px: kotlin.Double, py: kotlin.Double): kotlin.Double {
        return ptLineDistSq(x1, y1, x2, y2, px, py)
    }

    /**
     * Returns the square of the distance from a specified
     * `Point2D` to this line.
     * The distance measured is the distance between the specified
     * point and the closest point on the infinitely-extended line
     * defined by this `Line2D`.  If the specified point
     * intersects the line, this method returns 0.0.
     * @param pt the specified `Point2D` being measured
     * against this line
     * @return a double value that is the square of the distance from a
     * specified `Point2D` to the current
     * line.
     * @see .ptSegDistSq
     * @since 1.2
     */
    fun ptLineDistSq(pt: Point2D): kotlin.Double {
        return ptLineDistSq(
            x1, y1, x2, y2,
            pt.x, pt.y
        )
    }

    /**
     * Returns the distance from a point to this line.
     * The distance measured is the distance between the specified
     * point and the closest point on the infinitely-extended line
     * defined by this `Line2D`.  If the specified point
     * intersects the line, this method returns 0.0.
     *
     * @param px the X coordinate of the specified point being
     * measured against this line
     * @param py the Y coordinate of the specified point being
     * measured against this line
     * @return a double value that is the distance from a specified point
     * to the current line.
     * @see .ptSegDist
     * @since 1.2
     */
    fun ptLineDist(px: kotlin.Double, py: kotlin.Double): kotlin.Double {
        return ptLineDist(x1, y1, x2, y2, px, py)
    }

    /**
     * Returns the distance from a `Point2D` to this line.
     * The distance measured is the distance between the specified
     * point and the closest point on the infinitely-extended line
     * defined by this `Line2D`.  If the specified point
     * intersects the line, this method returns 0.0.
     * @param pt the specified `Point2D` being measured
     * @return a double value that is the distance from a specified
     * `Point2D` to the current line.
     * @see .ptSegDist
     * @since 1.2
     */
    fun ptLineDist(pt: Point2D): kotlin.Double {
        return ptLineDist(
            x1, y1, x2, y2,
            pt.x, pt.y
        )
    }

    /**
     * Tests if a specified coordinate is inside the boundary of this
     * `Line2D`.  This method is required to implement the
     * [Shape] interface, but in the case of `Line2D`
     * objects it always returns `false` since a line contains
     * no area.
     * @param x the X coordinate of the specified point to be tested
     * @param y the Y coordinate of the specified point to be tested
     * @return `false` because a `Line2D` contains
     * no area.
     * @since 1.2
     */
    fun contains(x: kotlin.Double, y: kotlin.Double): Boolean {
        return false
    }

    /**
     * Tests if a given `Point2D` is inside the boundary of
     * this `Line2D`.
     * This method is required to implement the [Shape] interface,
     * but in the case of `Line2D` objects it always returns
     * `false` since a line contains no area.
     * @param point the specified `Point2D` to be tested
     * @return `false` because a `Line2D` contains
     * no area.
     * @since 1.2
     */
    override operator fun contains(point: Point2D): Boolean {
        return false
    }

    /**
     * {@inheritDoc}
     * @since 1.2
     */
    fun intersects(x: kotlin.Double, y: kotlin.Double, w: kotlin.Double, h: kotlin.Double): Boolean {
        return intersects(Rectangle2D.Double(x, y, w, h))
    }

    /**
     * {@inheritDoc}
     * @since 1.2
     */
    override fun intersects(rect: Rectangle2D): Boolean {
        return rect.intersectsLine(x1, y1, x2, y2)
    }

    /**
     * Tests if the interior of this `Line2D` entirely contains
     * the specified set of rectangular coordinates.
     * This method is required to implement the `Shape` interface,
     * but in the case of `Line2D` objects it always returns
     * false since a line contains no area.
     * @param x the X coordinate of the upper-left corner of the
     * specified rectangular area
     * @param y the Y coordinate of the upper-left corner of the
     * specified rectangular area
     * @param w the width of the specified rectangular area
     * @param h the height of the specified rectangular area
     * @return `false` because a `Line2D` contains
     * no area.
     * @since 1.2
     */
    fun contains(x: kotlin.Double, y: kotlin.Double, w: kotlin.Double, h: kotlin.Double): Boolean {
        return false
    }

    /**
     * Tests if the interior of this `Line2D` entirely contains
     * the specified `Rectangle2D`.
     * This method is required to implement the `Shape` interface,
     * but in the case of `Line2D` objects it always returns
     * `false` since a line contains no area.
     * @param r the specified `Rectangle2D` to be tested
     * @return `false` because a `Line2D` contains
     * no area.
     * @since 1.2
     */
    operator fun contains(r: Rectangle2D?): Boolean {
        return false
    }

    /**
     * {@inheritDoc}
     * @since 1.2
     */
    override val bounds: Rectangle
        get() = bounds2D.bounds

    override val pathIterator: PathIterator
        get() {
            return LineIterator(this, null)
        }

    override fun getFlattenPathIterator(flatness: kotlin.Double): PathIterator {
        return LineIterator(this, null)
    }

    /**
     * Returns an iteration object that defines the boundary of this
     * `Line2D`.
     * The iterator for this class is not multi-threaded safe,
     * which means that this `Line2D` class does not
     * guarantee that modifications to the geometry of this
     * `Line2D` object do not affect any iterations of that
     * geometry that are already in process.
     * @param at the specified [AffineTransform]
     * @return a [PathIterator] that defines the boundary of this
     * `Line2D`.
     * @since 1.2
     */

    override fun getPathIterator(at: AffineTransform?): PathIterator {
        return LineIterator(this, at)
    }

    /**
     * Returns an iteration object that defines the boundary of this
     * flattened `Line2D`.
     * The iterator for this class is not multi-threaded safe,
     * which means that this `Line2D` class does not
     * guarantee that modifications to the geometry of this
     * `Line2D` object do not affect any iterations of that
     * geometry that are already in process.
     * @param at the specified `AffineTransform`
     * @param flatness the maximum amount that the control points for a
     * given curve can vary from colinear before a subdivided
     * curve is replaced by a straight line connecting the
     * end points.  Since a `Line2D` object is
     * always flat, this parameter is ignored.
     * @return a `PathIterator` that defines the boundary of the
     * flattened `Line2D`
     * @since 1.2
     */
    fun getPathIterator(at: AffineTransform?, flatness: kotlin.Double): PathIterator {
        return LineIterator(this, at)
    } //    /**

    //     * Creates a new object of the same class as this object.
    //     *
    //     * @return     a clone of this instance.
    //     * @exception  OutOfMemoryError            if there is not enough memory.
    //     * @see        java.lang.Cloneable
    //     * @since      1.2
    //     */
    //    public Object clone() {
    //        try {
    //            return super.clone();
    //        } catch (CloneNotSupportedException e) {
    //            // this shouldn't happen, since we are Cloneable
    //            throw new InternalError();
    //        }
    //    }
    companion object {
        /**
         * Returns an indicator of where the specified point
         * `(px,py)` lies with respect to the line segment from
         * `(x1,y1)` to `(x2,y2)`.
         * The return value can be either 1, -1, or 0 and indicates
         * in which direction the specified line must pivot around its
         * first end point, `(x1,y1)`, in order to point at the
         * specified point `(px,py)`.
         *
         * A return value of 1 indicates that the line segment must
         * turn in the direction that takes the positive X axis towards
         * the negative Y axis.  In the default coordinate system used by
         * Java 2D, this direction is counterclockwise.
         *
         * A return value of -1 indicates that the line segment must
         * turn in the direction that takes the positive X axis towards
         * the positive Y axis.  In the default coordinate system, this
         * direction is clockwise.
         *
         * A return value of 0 indicates that the point lies
         * exactly on the line segment.  Note that an indicator value
         * of 0 is rare and not useful for determining colinearity
         * because of floating point rounding issues.
         *
         * If the point is colinear with the line segment, but
         * not between the end points, then the value will be -1 if the point
         * lies "beyond `(x1,y1)`" or 1 if the point lies
         * "beyond `(x2,y2)`".
         *
         * @param x1 the X coordinate of the start point of the
         * specified line segment
         * @param y1 the Y coordinate of the start point of the
         * specified line segment
         * @param x2 the X coordinate of the end point of the
         * specified line segment
         * @param y2 the Y coordinate of the end point of the
         * specified line segment
         * @param px the X coordinate of the specified point to be
         * compared with the specified line segment
         * @param py the Y coordinate of the specified point to be
         * compared with the specified line segment
         * @return an integer that indicates the position of the third specified
         * coordinates with respect to the line segment formed
         * by the first two specified coordinates.
         * @since 1.2
         */
        fun relativeCCW(
            x1: kotlin.Double, y1: kotlin.Double,
            x2: kotlin.Double, y2: kotlin.Double,
            px: kotlin.Double, py: kotlin.Double
        ): Int {
            var x2 = x2
            var y2 = y2
            var px = px
            var py = py
            x2 -= x1
            y2 -= y1
            px -= x1
            py -= y1
            var ccw = px * y2 - py * x2
            if (ccw == 0.0) {
                // The point is colinear, classify based on which side of
                // the segment the point falls on.  We can calculate a
                // relative value using the projection of px,py onto the
                // segment - a negative value indicates the point projects
                // outside of the segment in the direction of the particular
                // endpoint used as the origin for the projection.
                ccw = px * x2 + py * y2
                if (ccw > 0.0) {
                    // Reverse the projection to be relative to the original x2,y2
                    // x2 and y2 are simply negated.
                    // px and py need to have (x2 - x1) or (y2 - y1) subtracted
                    //    from them (based on the original values)
                    // Since we really want to get a positive answer when the
                    //    point is "beyond (x2,y2)", then we want to calculate
                    //    the inverse anyway - thus we leave x2 & y2 negated.
                    px -= x2
                    py -= y2
                    ccw = px * x2 + py * y2
                    if (ccw < 0.0) {
                        ccw = 0.0
                    }
                }
            }
            return if (ccw < 0.0) -1 else if (ccw > 0.0) 1 else 0
        }

        /**
         * Tests if the line segment from `(x1,y1)` to
         * `(x2,y2)` intersects the line segment from `(x3,y3)`
         * to `(x4,y4)`.
         *
         * @param x1 the X coordinate of the start point of the first
         * specified line segment
         * @param y1 the Y coordinate of the start point of the first
         * specified line segment
         * @param x2 the X coordinate of the end point of the first
         * specified line segment
         * @param y2 the Y coordinate of the end point of the first
         * specified line segment
         * @param x3 the X coordinate of the start point of the second
         * specified line segment
         * @param y3 the Y coordinate of the start point of the second
         * specified line segment
         * @param x4 the X coordinate of the end point of the second
         * specified line segment
         * @param y4 the Y coordinate of the end point of the second
         * specified line segment
         * @return `true` if the first specified line segment
         * and the second specified line segment intersect
         * each other; `false` otherwise.
         * @since 1.2
         */
        fun linesIntersect(
            x1: kotlin.Double, y1: kotlin.Double,
            x2: kotlin.Double, y2: kotlin.Double,
            x3: kotlin.Double, y3: kotlin.Double,
            x4: kotlin.Double, y4: kotlin.Double
        ): Boolean {
            return relativeCCW(x1, y1, x2, y2, x3, y3) *
                    relativeCCW(x1, y1, x2, y2, x4, y4) <= 0 && relativeCCW(
                x3,
                y3,
                x4,
                y4,
                x1,
                y1
            ) *
                    relativeCCW(x3, y3, x4, y4, x2, y2) <= 0
        }

        /**
         * Returns the square of the distance from a point to a line segment.
         * The distance measured is the distance between the specified
         * point and the closest point between the specified end points.
         * If the specified point intersects the line segment in between the
         * end points, this method returns 0.0.
         *
         * @param x1 the X coordinate of the start point of the
         * specified line segment
         * @param y1 the Y coordinate of the start point of the
         * specified line segment
         * @param x2 the X coordinate of the end point of the
         * specified line segment
         * @param y2 the Y coordinate of the end point of the
         * specified line segment
         * @param px the X coordinate of the specified point being
         * measured against the specified line segment
         * @param py the Y coordinate of the specified point being
         * measured against the specified line segment
         * @return a double value that is the square of the distance from the
         * specified point to the specified line segment.
         * @see .ptLineDistSq
         * @since 1.2
         */
        fun ptSegDistSq(
            x1: kotlin.Double, y1: kotlin.Double,
            x2: kotlin.Double, y2: kotlin.Double,
            px: kotlin.Double, py: kotlin.Double
        ): kotlin.Double {
            // Adjust vectors relative to x1,y1
            // x2,y2 becomes relative vector from x1,y1 to end of segment
            var x2 = x2
            var y2 = y2
            var px = px
            var py = py
            x2 -= x1
            y2 -= y1
            // px,py becomes relative vector from x1,y1 to test point
            px -= x1
            py -= y1
            var dotprod = px * x2 + py * y2
            val projlenSq: kotlin.Double
            if (dotprod <= 0.0) {
                // px,py is on the side of x1,y1 away from x2,y2
                // distance to segment is length of px,py vector
                // "length of its (clipped) projection" is now 0.0
                projlenSq = 0.0
            } else {
                // switch to backwards vectors relative to x2,y2
                // x2,y2 are already the negative of x1,y1=>x2,y2
                // to get px,py to be the negative of px,py=>x2,y2
                // the dot product of two negated vectors is the same
                // as the dot product of the two normal vectors
                px = x2 - px
                py = y2 - py
                dotprod = px * x2 + py * y2
                projlenSq = if (dotprod <= 0.0) {
                    // px,py is on the side of x2,y2 away from x1,y1
                    // distance to segment is length of (backwards) px,py vector
                    // "length of its (clipped) projection" is now 0.0
                    0.0
                } else {
                    // px,py is between x1,y1 and x2,y2
                    // dotprod is the length of the px,py vector
                    // projected on the x2,y2=>x1,y1 vector times the
                    // length of the x2,y2=>x1,y1 vector
                    dotprod * dotprod / (x2 * x2 + y2 * y2)
                }
            }
            // Distance to line is now the length of the relative point
            // vector minus the length of its projection onto the line
            // (which is zero if the projection falls outside the range
            //  of the line segment).
            var lenSq = px * px + py * py - projlenSq
            if (lenSq < 0) {
                lenSq = 0.0
            }
            return lenSq
        }

        /**
         * Returns the distance from a point to a line segment.
         * The distance measured is the distance between the specified
         * point and the closest point between the specified end points.
         * If the specified point intersects the line segment in between the
         * end points, this method returns 0.0.
         *
         * @param x1 the X coordinate of the start point of the
         * specified line segment
         * @param y1 the Y coordinate of the start point of the
         * specified line segment
         * @param x2 the X coordinate of the end point of the
         * specified line segment
         * @param y2 the Y coordinate of the end point of the
         * specified line segment
         * @param px the X coordinate of the specified point being
         * measured against the specified line segment
         * @param py the Y coordinate of the specified point being
         * measured against the specified line segment
         * @return a double value that is the distance from the specified point
         * to the specified line segment.
         * @see .ptLineDist
         * @since 1.2
         */
        fun ptSegDist(
            x1: kotlin.Double, y1: kotlin.Double,
            x2: kotlin.Double, y2: kotlin.Double,
            px: kotlin.Double, py: kotlin.Double
        ): kotlin.Double {
            return sqrt(ptSegDistSq(x1, y1, x2, y2, px, py))
        }

        /**
         * Returns the square of the distance from a point to a line.
         * The distance measured is the distance between the specified
         * point and the closest point on the infinitely-extended line
         * defined by the specified coordinates.  If the specified point
         * intersects the line, this method returns 0.0.
         *
         * @param x1 the X coordinate of the start point of the specified line
         * @param y1 the Y coordinate of the start point of the specified line
         * @param x2 the X coordinate of the end point of the specified line
         * @param y2 the Y coordinate of the end point of the specified line
         * @param px the X coordinate of the specified point being
         * measured against the specified line
         * @param py the Y coordinate of the specified point being
         * measured against the specified line
         * @return a double value that is the square of the distance from the
         * specified point to the specified line.
         * @see .ptSegDistSq
         * @since 1.2
         */
        fun ptLineDistSq(
            x1: kotlin.Double, y1: kotlin.Double,
            x2: kotlin.Double, y2: kotlin.Double,
            px: kotlin.Double, py: kotlin.Double
        ): kotlin.Double {
            // Adjust vectors relative to x1,y1
            // x2,y2 becomes relative vector from x1,y1 to end of segment
            var x2 = x2
            var y2 = y2
            var px = px
            var py = py
            x2 -= x1
            y2 -= y1
            // px,py becomes relative vector from x1,y1 to test point
            px -= x1
            py -= y1
            val dotprod = px * x2 + py * y2
            // dotprod is the length of the px,py vector
            // projected on the x1,y1=>x2,y2 vector times the
            // length of the x1,y1=>x2,y2 vector
            val projlenSq = dotprod * dotprod / (x2 * x2 + y2 * y2)
            // Distance to line is now the length of the relative point
            // vector minus the length of its projection onto the line
            var lenSq = px * px + py * py - projlenSq
            if (lenSq < 0) {
                lenSq = 0.0
            }
            return lenSq
        }

        /**
         * Returns the distance from a point to a line.
         * The distance measured is the distance between the specified
         * point and the closest point on the infinitely-extended line
         * defined by the specified coordinates.  If the specified point
         * intersects the line, this method returns 0.0.
         *
         * @param x1 the X coordinate of the start point of the specified line
         * @param y1 the Y coordinate of the start point of the specified line
         * @param x2 the X coordinate of the end point of the specified line
         * @param y2 the Y coordinate of the end point of the specified line
         * @param px the X coordinate of the specified point being
         * measured against the specified line
         * @param py the Y coordinate of the specified point being
         * measured against the specified line
         * @return a double value that is the distance from the specified
         * point to the specified line.
         * @see .ptSegDist
         * @since 1.2
         */
        fun ptLineDist(
            x1: kotlin.Double, y1: kotlin.Double,
            x2: kotlin.Double, y2: kotlin.Double,
            px: kotlin.Double, py: kotlin.Double
        ): kotlin.Double {
            return sqrt(ptLineDistSq(x1, y1, x2, y2, px, py))
        }
    }
}